<script setup>
import { getI18n } from '@/plugins/i18n'
import { ref } from 'vue'

import { DxDataGrid } from 'devextreme-vue/data-grid'

import * as dataGrid from '@/composables/data-grid'

import { useRegistryStore } from '@/stores/registry.store'
import { useColumns } from '@/composables/registry/vehicle-movement-grid'

import CustomerDropDownBox from '@/components/shared/CustomerDropDownBox.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'

const props = defineProps({
  vehicle: {
    type: Object,
    required: true
  },
  entityLock: {
    type: Object,
    required: true
  },
})

const emits = defineEmits(['row-saved'])

const vehicleId = props.vehicle.data.id
const gridId = 'vehicle-movement-grid'
const gridRef = ref(null)

const i18n = getI18n()
const registryStore = useRegistryStore()

const formInstance = ref(null)

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.vehicleMovementsUrl,
  { filter: ['vehicle', '=', vehicleId] }
)
const implementedVehicleMovementTypes = registryStore.getLocalizedVehicleMovementTypes().filter(i => i.id === registryStore.vehicleMovementTypesEnum.OWNERSHIP_TRANSFER)

const rowButtons = props.entityLock.getEntityLockButtons({
  visible: {
    delete: (e) => {
      const gridInstance = gridRef.value?.instance
      const visibleRows = gridInstance?.getVisibleRows() || []
      return visibleRows.length > 1 && !e.isReadonly && !props.entityLock?.isLocked(false, vehicleId)
    }
  }
})

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '6rem',
    fixed: false
  },
  ...useColumns()
]

const { defaultActionTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  return `${defaultActionTitle(i)} ${i18n.t('common.vehicleMovement')}`
}

const onInitialized = (e) => {
  formInstance.value = e.component
}

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.vehicleInfo'),
      colCount: 4,
      items: [
        { dataField: 'vehicle_info.protocol', caption: i18n.t('common.protocol'), editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.status', editorOptions: { readOnly: true } },
        { itemType: 'empty', colSpan: 2 },
        { dataField: 'vehicle_info.type', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.fuel_type', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.engine_size', editorOptions: { readOnly: true } },
        { itemType: 'empty' },
        { dataField: 'vehicle_info.vin', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.engine_code', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.supplier_brand_ref', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.color', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.name', editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.notes', colSpan: 2, editorOptions: { readOnly: true } },
        { dataField: 'vehicle_info.plate_number', editorOptions: { readOnly: true } },
      ]
    },
    {
      itemType: 'group',
      caption: i18n.t('common.movementInfo'),
      colSpan: 2,
      colCount: 4,
      items: [
        {
          dataField: 'movement_type',
          caption: i18n.t('common.type'),
          editorOptions: {
            dataSource: implementedVehicleMovementTypes,
            valueExpr: 'id',
            displayExpr: 'name',
            showClearButton: true,
          }
        },
        { dataField: 'movement_datetime', caption: i18n.t('common.date'), width: '10rem', dataType: 'datetime' },
        { dataField: 'customer_id'},
      ]
    },
  ],
  onInitialized: onInitialized,
}, {
  allowAdding: true,
  allowUpdating: (e) => !props.entityLock?.isLocked(false, vehicleId),
  allowDeleting: (e) => !props.entityLock?.isLocked(false, vehicleId),
  onInitializedCustom: (e) => props.entityLock.readOnlyToolbarItems(e),
})

const getVehicleInfo = () => ({
  protocol: props.vehicle.data.id,
  color: props.vehicle.data.color,
  engine_code: props.vehicle.data.engine_code,
  engine_size: props.vehicle.data.engine_size,
  fuel_type: props.vehicle.data.fuel_type,
  name: props.vehicle.data.name,
  plate_number: props.vehicle.data.plate_number,
  status: props.vehicle.data.status,
  vin: props.vehicle.data.vin,
  notes: props.vehicle.data.notes,
  type: props.vehicle.data.vehicle_type,
  supplier_brand_ref: props.vehicle.data.supplier_brand_ref
})

const onRowInserting = (e) => {
  const { movement_datetime, movement_type, customer_id, vehicle } = e.data

  e.data = {
    movement_datetime,
    movement_type,
    customer_id,
    vehicle
  }
}

const onSaved = (e) => emits('row-saved', props.vehicle?.row.data.id, props.vehicle?.row.key)

const onInitNewRow = (e) => {
  e.data.vehicle = vehicleId
  e.data.movement_type = registryStore.vehicleMovementTypesEnum.OWNERSHIP_TRANSFER
  e.data.movement_datetime = new Date().toISOString()

  e.data.vehicle_info = getVehicleInfo()
}

const onEditingStart = (e) => {
  e.data.vehicle_info = getVehicleInfo()
}

const onRowRemoved = () => {
  const gridInstance = gridRef.value?.instance
  gridInstance?.refresh()
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  height: '100%',
  onInitNewRow: onInitNewRow,
  onRowInserting: onRowInserting,
  onSaved: onSaved,
  onRowRemoved: onRowRemoved,
  onEditingStart: onEditingStart,
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
      class="q-mb-lg"
    >
      <template #customerEditor="{ data: cellInfo }">
        <customer-drop-down-box
          show-clear-button
          :data="cellInfo"
          :read-only="cellInfo.data?.has_worksheet"
          :exclude="props.vehicle.data?.customer_id"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'Vehicle'"
          :master-entity-id="vehicleId"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :master-entity-id="vehicleId"
          :grid="gridRef"
          :entity-lock="props.entityLock"
        />
      </template>
    </dx-data-grid>
  </div>
</template>
<style>
</style>
